<script>
/**
 * Alocação da carteira por classe: uma barra segmentada, cada pedaço uma classe.
 *
 * ★ Mesma anatomia da barra do AuC no painel do escritório — é o desenho que a
 * lateral já usa para "como um total se divide". O que muda é a INTERAÇÃO: lá
 * são três fatias e a legenda cabe embaixo; aqui são oito ou nove, e uma lista
 * de nove linhas de texto ocupava mais altura que o gráfico inteiro.
 *
 * ⚠️ Por isso o nome da classe aparece no HOVER, numa linha só que se atualiza,
 * em vez de uma legenda estática. Sem ela, a barra vira decoração colorida: o
 * usuário vê que há sete pedaços e não sabe o que nenhum deles é. Em repouso a
 * linha mostra a MAIOR fatia, que é a resposta mais provável para "onde está o
 * dinheiro deste cliente".
 *
 * Componente BURRO: recebe fatias com cor resolvida e só desenha.
 */
import { toPercent } from '@utils/Format'

const CASAS_DO_PERCENTUAL = 0
const ZERO = 0

export default {
  name: 'IaClienteAlocacao',
  props: {
    // `[{ label, valor, cor }]`, ordenadas da maior para a menor.
    fatias: { type: Array, default: () => [] },
  },
  data() {
    return { indiceEmFoco: null }
  },
  computed: {
    // Em repouso, a maior fatia. Ela é a primeira porque a lista já vem
    // ordenada — ver `alocacaoPorClasse`.
    emFoco() {
      const indice = this.indiceEmFoco === null ? ZERO : this.indiceEmFoco
      return this.fatias[indice] || null
    },
    rotuloEmFoco() {
      if (!this.emFoco) return ''
      return (
        this.emFoco.label +
        ' · ' +
        toPercent(this.emFoco.valor, CASAS_DO_PERCENTUAL)
      )
    },
    pedacos() {
      return this.fatias.map((fatia, indice) => ({
        chave: fatia.label + ':' + indice,
        indice,
        // `flex` proporcional resolve a largura sem precisar somar 100 na mão.
        estilo: { flex: fatia.valor, background: fatia.cor },
        titulo:
          fatia.label + ' · ' + toPercent(fatia.valor, CASAS_DO_PERCENTUAL),
      }))
    },
  },
  methods: {
    focar(indice) {
      this.indiceEmFoco = indice
    },
    // Volta para a maior, não para o vazio: a linha some e a barra fica muda.
    desfocar() {
      this.indiceEmFoco = null
    },
  },
}
</script>

<template>
  <div class="ia-cliente-alocacao" @mouseleave="desfocar">
    <div class="ia-cliente-alocacao__barra">
      <!--
        `<button>` e não `<span>`: o pedaço é alvo de interação, e sem foco de
        teclado a única forma de saber o nome de uma classe seria com o mouse.
        `title` cobre o tooltip nativo, para quem chega pelo toque.
      -->
      <button
        v-for="pedaco in pedacos"
        :key="pedaco.chave"
        type="button"
        class="ia-cliente-alocacao__pedaco"
        :style="pedaco.estilo"
        :title="pedaco.titulo"
        :aria-label="pedaco.titulo"
        @mouseenter="focar(pedaco.indice)"
        @focus="focar(pedaco.indice)"
        @blur="desfocar"
      />
    </div>

    <span v-if="rotuloEmFoco" class="ia-cliente-alocacao__rotulo ia-texto-auxiliar">
      {{ rotuloEmFoco }}
    </span>
  </div>
</template>

<style lang="sass" scoped>
.ia-cliente-alocacao
  display: flex
  flex-direction: column
  gap: 6px

.ia-cliente-alocacao__barra
  display: flex
  gap: 3px
  height: 8px

.ia-cliente-alocacao__pedaco
  min-width: 3px
  padding: 0
  border: 0
  border-radius: 3px
  cursor: pointer
  transition: opacity 0.12s var(--motion-easing-standard)

  &:focus-visible
    outline: 2px solid var(--primary)
    outline-offset: 2px

// ★ O apagamento mora no PAI e vale para todos; o pedaço sob o cursor se
// reacende. Assim a regra é uma só, em vez de "apague todos menos este", que em
// CSS exigiria um seletor por índice.
//
// ⚠️ `:focus-within` junto de `:hover`: quem navega por teclado tem de ver o
// mesmo destaque, senão o `<button>` seria alvo sem retorno visual.
.ia-cliente-alocacao__barra:hover .ia-cliente-alocacao__pedaco,
.ia-cliente-alocacao__barra:focus-within .ia-cliente-alocacao__pedaco
  opacity: 0.3

// ⚠️ Especificidade DELIBERADA (0,3,0 contra os 0,2,0 da regra acima). Empatado,
// quem ganhava era a ordem no arquivo — frágil demais para a única regra que
// mantém o pedaço escolhido aceso enquanto os outros apagam.
.ia-cliente-alocacao__barra .ia-cliente-alocacao__pedaco:hover,
.ia-cliente-alocacao__barra .ia-cliente-alocacao__pedaco:focus-visible
  opacity: 1

// Uma linha, sempre a mesma altura: se ela sumisse no repouso, a coluna pularia
// a cada passagem do mouse.
.ia-cliente-alocacao__rotulo
  color: var(--neutral-700)
  overflow: hidden
  text-overflow: ellipsis
  white-space: nowrap
  font-variant-numeric: tabular-nums

@media (prefers-reduced-motion: reduce)
  .ia-cliente-alocacao__pedaco
    transition: none
</style>
